import { ActionContext, ActionTree } from "vuex";
import { RootState } from "@/store/state";
import { ReportsState } from "./state";
import { reportsActions, reportsGetters, reportsMutations } from "./types";
import { log } from "@/core/logger";
import { GET, POST, PUT } from "@/core/http/methods";
import moment from "moment";
import { CaseReport, Observation, SlideReport, SlidesReports } from "@/store/reports/models";
import { ISlide } from "@/models/slide";
import { WorkflowState } from "@/models/workflow-state";

type Context = ActionContext<ReportsState, RootState>;

const actions: ActionTree<ReportsState, RootState> = {
  /** Reports **/

  async [reportsActions.SET_SLIDE_OBSERVATIONS](
    context: Context,
    payload: { caseId: string; slideId: string; slideReport: SlideReport }
  ): Promise<void> {
    if (payload.caseId && payload.slideId && payload.slideReport) {
      try {
        log(`setting slide observations for slide ${payload.slideId}`);
        const caseReport = context.state.casesReports[payload.caseId];
        if (!caseReport) {
          await context.dispatch(reportsActions.CREATE_EMPTY_CASE_REPORT, {
            caseId: payload.caseId,
          });
        }
        context.commit(reportsMutations.UPDATE_SLIDE_OBSERVATIONS, {
          caseId: payload.caseId,
          slideId: payload.slideId,
          slideReport: payload.slideReport,
        });
      } catch (error) {
        log("failed setting slide observations", error);
      }
    }
  },

  async [reportsActions.DELETE_SLIDE_REPORT](
    context: Context,
    payload: {
      slideId: string;
    }
  ): Promise<void> {
    const caseId = context.rootState.activeCaseId;
    context.commit(reportsMutations.DELETE_SLIDE_REPORT, {
      caseId,
      slideId: payload.slideId,
    });
  },

  async [reportsActions.UPDATE_REPORT](context: Context) {
    try {
      const caseId = context.rootState.activeCaseId;
      log(`updating report for case ${caseId}`);
      const reportId = context.state.activeReportId;
      const caseReport = context.getters[reportsGetters.CASE_REPORT](caseId);
      const updatedReport = await PUT(`report/cases/${caseId}/${reportId}`, caseReport);
      log(`case report ${caseId} was updated on the server with`, updatedReport.data.data);
      return updatedReport;
    } catch (error) {
      log("error updating report", error);
      throw error;
    }
  },

  async [reportsActions.PUBLISH_EMPTY_CASE_REPORT](
    context: Context,
    payload: { caseId: string }
  ): Promise<any> {
    try {
      log(`initializing report for case: ${payload.caseId}`);
      const reportData = await POST(
        `report/cases/${payload.caseId}`,
        context.state.casesReports[payload.caseId]
      );
      if (reportData.data?.data?.report_id) {
        context.commit(reportsMutations.SET_ACTIVE_CASE_REPORT_ID, {
          reportId: reportData.data.data.report_id,
        });
      }
      log(`successfully initialized report`);
      return reportData;
    } catch (error) {
      log("error getting report id");
    }
  },

  // TODO: Remove Temporary Action:
  async [reportsActions.CHECK_FOR_DATA_MISMATCH](
    context: Context,
    payload: { slidesReports: SlidesReports }
  ): Promise<boolean> {
    let isDataMismatch = false;
    const slidesReports = payload.slidesReports;
    for (const slideId in slidesReports) {
      const slide: ISlide = context.rootGetters.GET_SLIDE_BY_ID(slideId);
      isDataMismatch ||=
        slidesReports[slideId].isComplete != (slide?.workflowState === WorkflowState.COMPLETE);
    }
    return isDataMismatch;
  },

  // TODO: Remove Temporary Action:
  async [reportsActions.SYNC_WORKFLOWSTATE_AND_IS_COMPLETE](
    context: Context,
    payload: { slidesReports: SlidesReports }
  ): Promise<SlidesReports> {
    const slidesReports = payload.slidesReports;
    for (const slideId in slidesReports) {
      let slide: ISlide = null;
      const interval = setInterval(() => {
        console.log("interval beginning");
        slide = context.rootGetters.GET_SLIDE_BY_ID(slideId);
        if (slide) {
          slidesReports[slideId].isComplete = slide.workflowState === WorkflowState.COMPLETE;
          clearInterval(interval);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
      }, 10000);
    }
    return slidesReports;
  },

  async [reportsActions.LOAD_OR_CREATE_REPORT](
    context: Context,
    payload: { caseId: string }
  ): Promise<any> {
    let reportData = await context.dispatch(reportsActions.FETCH_REPORT, {
      caseId: payload.caseId,
    });
    // reportData.data.data.data.slidesReports = {} // <- Mocking empty slide reports
    let reportId = reportData.data?.data?.report_id;
    if (reportId) {
      // TODO: The following is a hack to resolve data consistency issue in v3.0
      //  This must be removed as part of v3.2
      const isDataMismatch = await context.dispatch(reportsActions.CHECK_FOR_DATA_MISMATCH, {
        slidesReports: reportData.data.data.data.slidesReports,
      });
      if (isDataMismatch) {
        reportData.data.data.data.slidesReports = await context.dispatch(
          reportsActions.SYNC_WORKFLOWSTATE_AND_IS_COMPLETE,
          {
            slidesReports: reportData.data.data.data.slidesReports,
          }
        );
      }
      // TODO: Pause
      await context.commit(reportsMutations.SET_ACTIVE_CASE_REPORT_ID, { reportId });
      context.commit(reportsMutations.SET_CASE_REPORT, {
        caseId: payload.caseId,
        caseReport: reportData.data?.data?.data,
      });
      // TODO: continue
      if (isDataMismatch) {
        await context.dispatch(reportsActions.UPDATE_REPORT);
      }
      // TODO: End
    } else {
      await context.dispatch(reportsActions.CREATE_EMPTY_CASE_REPORT, {
        caseId: payload.caseId,
      });
      await context.dispatch(reportsActions.PUBLISH_EMPTY_CASE_REPORT, {
        caseId: payload.caseId,
      });
    }
  },

  async [reportsActions.FETCH_REPORT](context: Context, payload: { caseId: string }): Promise<any> {
    log(`fetching report for case ${payload.caseId}`);
    try {
      return GET(`report/cases/${payload.caseId}`);
    } catch (error: any) {
      log(`failed fetching report`, error);
    }
  },

  [reportsActions.CREATE_EMPTY_CASE_REPORT](context: Context, payload: { caseId: string }): void {
    log(`initializing report for case ${payload.caseId}`);
    context.commit(reportsMutations.SET_CASE_REPORT, {
      caseId: payload.caseId,
      caseReport: {
        observations: [],
        comments: "",
        isComplete: false,
        completedOn: null,
        completedBy: "",
        slidesReports: {},
      },
    });
  },

  [reportsActions.CREATE_EMPTY_SLIDE_REPORT](
    context: Context,
    payload: { caseId: string; slideId: string }
  ): void {
    log(`initializing report for slide ${payload.slideId}`);
    context.commit(reportsMutations.SET_SLIDE_REPORT, {
      caseId: payload.caseId,
      slideId: payload.slideId,
      slideReport: {
        observations: [],
        comments: {
          value: "",
          title: "",
        },
        isComplete: false,
      },
    });
  },

  [reportsActions.SET_REPORT](context: Context, payload: { reportId: string }): void {
    context.commit(reportsMutations.SET_ACTIVE_CASE_REPORT_ID, payload);
  },

  async [reportsActions.SET_SLIDE_COMPLETE](
    context: Context,
    payload: { isComplete: boolean }
  ): Promise<void> {
    const caseId = context.rootState.activeCaseId;
    const slideId = context.rootState.activeSlideId;
    const caseReport: CaseReport = context.state.casesReports[caseId];
    if (!caseReport) {
      await context.dispatch(reportsActions.CREATE_EMPTY_CASE_REPORT, { caseId });
    }
    if (!caseReport.slidesReports[slideId]) {
      await context.dispatch(reportsActions.CREATE_EMPTY_SLIDE_REPORT, { caseId, slideId });
    }
    context.commit(reportsMutations.SET_SLIDE_COMPLETE, {
      caseId,
      slideId,
      isComplete: payload.isComplete,
    });
  },

  async [reportsActions.UPDATE_CASE_COMMENTS](
    context: Context,
    payload: { caseId: string; comments: string }
  ): Promise<void> {
    try {
      log(`updating comments for case report ${payload.caseId}`);
      await context.commit(reportsMutations.UPDATE_CASE_COMMENTS, {
        caseId: payload.caseId,
        comments: payload.comments,
      });
      return await context.dispatch(reportsActions.UPDATE_REPORT);
    } catch (error: any) {
      log(`failed updating comments for case report ${payload.caseId}. Reason:`, error);
    }
  },

  async [reportsActions.UPDATE_CASE_COMPLETENESS](
    context: Context,
    payload: { caseId: string; isComplete: boolean }
  ): Promise<void> {
    // TODO: Refactor Case Workflow State Logic
    try {
      log(`setting is complete state to ${payload.isComplete} for case ${payload.caseId}`);
      const currentDate = moment().unix();
      await context.commit(reportsMutations.UPDATE_CASE_COMPLETENESS, {
        caseId: payload.caseId,
        isComplete: payload.isComplete,
        completedOn: payload.isComplete ? currentDate : null,
        completedBy: payload.isComplete ? context.rootState.loggedInUserName : null,
      });
      return await context.dispatch(reportsActions.UPDATE_REPORT);
    } catch (error: any) {
      log(`failed updating is complete state for case report ${payload.caseId}. Reason:`, error);
    }
  },

  async [reportsActions.INIT_CASE_OBSERVATIONS](context: Context): Promise<any> {
    try {
      const activeCaseId = context.rootState.activeCaseId;

      // prepare observations config
      const insightsConfig = await context.dispatch(
        reportsActions.GET_OR_FETCH_RESOURCE,
        { resourceName: "insightsConfig", uri: "config/insights" },
        { root: true }
      );
      const activeCaseTissue = context.rootGetters.GET_ACTIVE_CASE_TISSUE;
      const caseObservationsConfig = insightsConfig.case_insights[activeCaseTissue];

      // sync report to store
      await context.dispatch(reportsActions.LOAD_OR_CREATE_REPORT, {
        caseId: activeCaseId,
      });

      // prepare observations data
      const activeCaseObservations = caseObservationsConfig.map((observationSchema: any) => {
        const observationValue = context.state.casesReports[activeCaseId].observations.find(
          (observation: Observation) => {
            return observation.key === observationSchema.key;
          }
        );
        return {
          ...observationSchema,
          value: observationValue?.value,
          title: observationValue?.title,
        };
      });
      context.commit(reportsMutations.SET_CASE_OBSERVATIONS, {
        caseId: activeCaseId,
        observations: activeCaseObservations,
      });
      return activeCaseObservations;
    } catch (error: any) {
      log(`failed initializing case observations`, error);
    }
  },

  async [reportsActions.UPDATE_CASE_OBSERVATION](
    context: Context,
    payload: { observationKey: string; observationValue: string; observationTitle: string }
  ): Promise<void> {
    const activeCaseId = context.rootState.activeCaseId;
    const caseObservations = context.state.casesReports[activeCaseId].observations;
    for (const observation of caseObservations) {
      if (observation.key === payload.observationKey) {
        // replaces only the given observation
        observation.value = payload.observationValue;
        observation.title = payload.observationTitle;
        break;
      }
    }
    context.commit(reportsMutations.SET_CASE_OBSERVATIONS, {
      caseId: activeCaseId,
      observations: caseObservations,
    });
    return await context.dispatch(reportsActions.UPDATE_REPORT);
  },
};

export default actions;
